import React, { useState } from "react";
import { Field, ErrorMessage } from "formik";
import { JsonEditor } from "json-edit-react";
import { get } from "lodash";
import { Code2, Copy, Check, Trash2, RefreshCw } from "lucide-react";

const FormJsonEditor = ({
  label = "JSON Editor",
  labelClassName = "",
  name = "jsonField",
  className = "",
  required = false,
  minHeight = "240px",
  readOnly = false,
  rootName = "root",
  ...props
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (data) => {
    try {
      const content =
        typeof data === "string" ? data : JSON.stringify(data, null, 2);
      navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy JSON:", err);
    }
  };

  const getItemCount = (data) => {
    if (!data) return 0;
    if (Array.isArray(data)) return data.length;
    if (typeof data === "object") return Object.keys(data).length;
    return 1;
  };

  return (
    <div className={`w-full flex flex-col gap-1.5 ${className}`}>
      <Field name={name}>
        {({ field, form, meta }) => {
          const rawValue = get(form.values, name);

          // Parse string input into object if needed for json-edit-react
          let parsedData = rawValue;
          if (typeof rawValue === "string" && rawValue.trim() !== "") {
            try {
              parsedData = JSON.parse(rawValue);
            } catch (e) {
              parsedData = rawValue;
            }
          }

          const safeData = parsedData ?? {};
          const isInvalid = meta.touched && meta.error;

          const handleChange = (updatedJson) => {
            // Returns object directly or serialized based on requirements
            const finalValue = updatedJson?.newData ?? updatedJson;
            form.setFieldValue(name, finalValue);
            form.setFieldTouched(name, true, false);
          };

          const handleClear = () => {
            form.setFieldValue(name, {});
            form.setFieldTouched(name, true, false);
          };

          return (
            <div className="flex flex-col gap-1.5 w-full">
              {/* Header Label and Toolbar */}
              <div className="flex items-center justify-between gap-2">
                {label && (
                  <label
                    htmlFor={name}
                    className={`text-sm font-semibold text-gray-800 capitalize flex items-center gap-1.5 ${
                      isInvalid ? "text-red-500" : labelClassName
                    }`}
                  >
                    <Code2 className="w-4 h-4 text-primary" />
                    {label}
                    {required && <span className="text-red-500">*</span>}
                  </label>
                )}

                {/* Header Action Bar */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 border border-gray-200">
                    {getItemCount(safeData)}{" "}
                    {getItemCount(safeData) === 1 ? "item" : "items"}
                  </span>

                  <button
                    type="button"
                    onClick={() => handleCopy(safeData)}
                    className="p-1 rounded-md text-gray-500 hover:text-primary hover:bg-gray-100 transition"
                    title="Copy JSON"
                  >
                    {copied ? (
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>

                  {!readOnly && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="p-1 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                      title="Clear JSON"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* JSON Editor Window Shell */}
              <div
                className={`relative rounded-xl border bg-gray-50/50 transition-all overflow-hidden ${
                  isInvalid
                    ? "border-red-300 ring-2 ring-red-500/10"
                    : "border-gray-200 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20"
                }`}
                style={{ minHeight }}
              >
                <div className="p-3 overflow-auto max-h-[500px] text-xs font-mono">
                  <JsonEditor
                    data={safeData}
                    onUpdate={handleChange}
                    restrictEdit={readOnly}
                    restrictAdd={readOnly}
                    restrictDelete={readOnly}
                    rootName={rootName}
                    // theme="github"
                    {...props}
                  />
                </div>
              </div>

              {/* Error Message Footer */}
              <div className="min-h-[1.25rem] px-1">
                <ErrorMessage name={name}>
                  {(msg) => (
                    <span className="block text-xs font-medium text-red-500">
                      {msg}
                    </span>
                  )}
                </ErrorMessage>
              </div>
            </div>
          );
        }}
      </Field>
    </div>
  );
};

export default FormJsonEditor;
